import React, { useEffect, useRef, useState } from 'react';
import { TextField, Popper, Paper, makeStyles, CircularProgress, MenuList, MenuItem } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import IconButton from '@material-ui/core/IconButton';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { debounce } from 'lodash';

const useStyles = makeStyles(theme => ({
  popper: {
    zIndex: theme.zIndex.modal + 1,
    minWidth: 300,
    maxWidth: '80vw'
  },
  paper: {
    maxHeight: '70vh',
    overflowY: 'auto'
  }
}));
const DEBOUNCE_TIME = 700;

export const SearchInput = ({
  value,
  options = [],
  onSearch,
  onSelect,
  isLoading = false,
  label,
  getOptionLabel = option => option.name || option
}) => {
  const classes = useStyles();
  const { messages: intlMessages } = useIntl();
  const inputRef = useRef();
  const [showOptions, setShowOptions] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const lastSearchValue = useRef('');

  const debouncedSearchRef = useRef(
    debounce(value => {
      if (value.trim() !== lastSearchValue.current) {
        lastSearchValue.current = value.trim();
        onSearch(value);
      }
    }, DEBOUNCE_TIME)
  );

  useEffect(() => {
    const debouncedSearch = debouncedSearchRef.current;

    return () => {
      debouncedSearch.cancel();
    };
  }, []);

  const handlerOnSelect = option => {
    const selectedValue = getOptionLabel(option);
    onSelect && onSelect(option);
    setInputValue(selectedValue);
    setShowOptions(false);
    lastSearchValue.current = selectedValue;
  };

  const handleClearInput = () => {
    setInputValue('');
    onSearch('');
    lastSearchValue.current = '';
    setShowOptions(false);
  };

  useEffect(() => {
    if (inputRef.current) {
      const shouldShow = inputRef.current.value.trim().length > 0 && options.length > 0;

      setShowOptions(shouldShow);
    }
  }, [options]);

  useEffect(() => {
    if (!value || value.length === 0) {
      handleClearInput();
    }
  }, [value]);

  return (
    <div>
      <TextField
        inputRef={inputRef}
        label={label || intlMessages['common.search']}
        variant="outlined"
        size="small"
        value={inputValue}
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <>
              {inputValue && (
                <IconButton onClick={handleClearInput} size="small">
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              {isLoading && <CircularProgress color="inherit" size={20} />}
            </>
          )
        }}
        onChange={e => {
          setInputValue(e.target.value);
          debouncedSearchRef.current(e.target.value);
        }}
      />
      <Popper anchorEl={inputRef.current} className={classes.popper} placement="bottom-start" open={showOptions}>
        <Paper className={classes.paper}>
          <MenuList>
            {options.map((option, index) => (
              <MenuItem key={index} onClick={() => handlerOnSelect(option)}>
                {getOptionLabel(option)}
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>
    </div>
  );
};

SearchInput.propTypes = {
  options: PropTypes.array,
  onSearch: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.string,
  getOptionLabel: PropTypes.func
};
